import type { SubmissionMethod } from '@/services/api/generated/taskActivityService';
import { TreatmentRecordRequestMetadataSchema } from '@/services/api/generated/treatmentRecordRequestsService';

import type { AlternateContact } from './treatment-request-alternates';

export type ContactSubmissionMethod = SubmissionMethod | string;

export interface ContactDiscoverySummary {
  call_summary?: string | null;
  submission_methods?: ContactSubmissionMethod[] | null;
  required_documents?: string[] | null;
  alternate_contacts?: AlternateContact[] | null;
  notes?: string | null;
}

/**
 * Alternate contact discovered by Voice AI during a provider call
 * Used when a request is auto-retried with a new fax/address
 */
export interface AutoRetriedAlternateContact {
  handle_type: 'fax' | 'mailing_address';
  value: string;
  confidence: number;
}

/**
 * Metadata fields present on requests with status 'auto_retried'
 * Set when Voice AI discovers an alternate fax during a provider call
 * and automatically forks the request to resend to the new fax
 */
export interface AutoRetriedMetadata {
  /** Reason for the auto-retry, e.g., "alternate_fax_discovered" */
  voice_ai_fork_reason?: string;
  /** UUID of the new request that was forked from this one */
  forked_to_request_id?: string;
  /** The alternate contact that was discovered */
  alternate_contact?: AutoRetriedAlternateContact;
}

export interface LegacyVoiceAiAlternate {
  value?: string | null;
  handle_type?: string | null;
  label?: string | null;
  confidence?: number | null;
  type?: string | null;
}

/**
 * Extended metadata schema for treatment record requests
 * Includes Voice AI scheduling fields and auto-retry information
 */
export interface ExtendedTreatmentRecordRequestMetadata
  extends Omit<
    TreatmentRecordRequestMetadataSchema,
    'voice_ai_discovered_alternates' | 'contact_discovery_summary'
  > {
  /** Number of days between Voice AI follow-up calls (1-7, max weekly) */
  voice_ai_check_interval_days?: number;
  /** ISO string timestamp of the last voice AI call */
  last_called_at?: string | null;
  /** When status last changed to active state */
  status_changed_at?: string | null;
  /** Initial submission timestamp */
  request_submitted_at?: string | null;
  /** Next scheduled Voice AI call from backend workflow */
  next_scheduled_call?: string | null;
  /** Aggregated results from the contact discovery workflow */
  contact_discovery_summary?: ContactDiscoverySummary | null;
  /** Timestamp for the latest metadata refresh */
  contact_discovery_last_updated_at?: string | null;
  /** Legacy Voice AI paragraph summary */
  voice_ai_call_summary?: string | null;
  /** Legacy alternates when new schema is missing */
  voice_ai_discovered_alternates?: LegacyVoiceAiAlternate[] | null;
  /** Whether this TRR was auto-created for a one-off provider (Hospital, EMS, Urgent Care) */
  auto_created_for_one_off?: boolean;
  /** The inferred provider type that triggered auto-creation (e.g. HOSPITAL, EMS, URGENT_CARE) */
  inferred_provider_type?: string | null;
  /** Reason for auto-retry (when status is 'auto_retried') */
  voice_ai_fork_reason?: string;
  /** UUID of the new request that was forked from this one (when status is 'auto_retried') */
  forked_to_request_id?: string;
  /** The alternate contact discovered by Voice AI (when status is 'auto_retried') */
  alternate_contact?: AutoRetriedAlternateContact;
}

/**
 * Helper to safely access extended metadata fields
 */
export function getExtendedMetadata(
  metadata: any,
): ExtendedTreatmentRecordRequestMetadata {
  return metadata || {};
}

/**
 * Minimal interface for TRR fields needed to determine HIPAA sent date.
 * This avoids importing the full schema and allows flexibility.
 */
export interface HipaaSentDateRequest {
  email_transmission_status?: {
    sent_at?: string | null;
  } | null;
  fax_transmission_status?: {
    sent_at?: string | null;
  } | null;
}

/**
 * Get the accurate HIPAA package sent date from a treatment record request.
 *
 * Priority order:
 * 1. email_transmission_status.sent_at (for email submissions)
 * 2. fax_transmission_status.sent_at (for fax submissions)
 *
 * Returns null if no accurate sent date is available.
 * Historical submissions (~480 email records) will return null since
 * transmission status fields are not backfilled.
 *
 * Note: hipaa_package_locked_at is intentionally NOT used as fallback
 * because it's unreliable for historical records.
 */
export function getHipaaSentDate(
  request: HipaaSentDateRequest | null | undefined,
): string | null {
  if (!request) return null;

  // 1. Email transmission status (for email submissions)
  if (request.email_transmission_status?.sent_at) {
    return request.email_transmission_status.sent_at;
  }

  // 2. Fax transmission status (for fax submissions)
  if (request.fax_transmission_status?.sent_at) {
    return request.fax_transmission_status.sent_at;
  }

  // No accurate sent date available for historical records
  return null;
}
